import React, { useState, useEffect, useCallback } from 'react';
import { Role } from '../../types';
import { PermissionOverride } from '../../types/permissions';
import { getRolePermissions, updateRolePermissions } from '../../api/permissions';
import { ROLE_LABELS } from '../../utils/roles';
import { PermissionMatrix } from './PermissionMatrix';

const CONFIGURABLE_ROLES: Role[] = [
  'admin', 'integrator', 'dealer', 'customer', 'property_manager', 'technician', 'viewer', 'tenant'
];

export const RolePermissionsTab: React.FC = () => {
  const [selectedRole, setSelectedRole] = useState<Role>('admin');
  const [overrides, setOverrides] = useState<PermissionOverride[]>([]);
  const [originalOverrides, setOriginalOverrides] = useState<PermissionOverride[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchOverrides = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getRolePermissions(selectedRole);
      setOverrides(data);
      setOriginalOverrides(JSON.parse(JSON.stringify(data)));
    } catch (err) {
      console.error('[RBAC] Failed to load role permissions:', err);
      setError(err instanceof Error ? err.message : 'Failed to load role permissions.');
      setOverrides([]);
      setOriginalOverrides([]);
    } finally {
      setLoading(false);
    }
  }, [selectedRole]);

  useEffect(() => {
    fetchOverrides();
  }, [fetchOverrides]);

  const hasChanges = JSON.stringify(overrides) !== JSON.stringify(originalOverrides);

  const handleChange = (page: string, operation: string, value: boolean | null) => {
    setOverrides(prev => {
      const existing = prev.find(o => o.page === page);
      const key = `can_${operation}` as keyof PermissionOverride;

      if (existing) {
        return prev.map(o =>
          o.page === page ? { ...o, [key]: value } : o
        );
      } else {
        const newOverride: PermissionOverride = {
          page,
          can_view: null,
          can_create: null,
          can_update: null,
          can_delete: null,
          can_export: null,
          [key]: value,
        };
        return [...prev, newOverride];
      }
    });
  };

  const handleReset = (page: string) => {
    setOverrides(prev => prev.filter(o => o.page !== page));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const meaningful = overrides.filter(o =>
        o.can_view !== null || o.can_create !== null || o.can_update !== null ||
        o.can_delete !== null || o.can_export !== null
      );
      await updateRolePermissions(selectedRole, meaningful);
      const authoritative = await getRolePermissions(selectedRole);
      setOriginalOverrides(JSON.parse(JSON.stringify(authoritative)));
      setOverrides(authoritative);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('[RBAC] Failed to save role permissions:', err);
      setError(err instanceof Error ? err.message : 'Failed to save role permissions.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-4 mb-6">
        <label className="text-sm font-medium text-gray-700">Role:</label>
        <select
          value={selectedRole}
          onChange={e => setSelectedRole(e.target.value as Role)}
          disabled={loading || saving}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
        >
          {CONFIGURABLE_ROLES.map(role => (
            <option key={role} value={role}>
              {ROLE_LABELS[role]}
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center gap-6 mb-4 text-xs text-gray-500">
        <div className="flex items-center gap-1.5">
          <span className="w-5 h-5 rounded border-2 bg-green-500 border-green-500 inline-flex items-center justify-center text-white">
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
          </span>
          Granted
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-5 h-5 rounded border-2 bg-white border-red-300 inline-flex items-center justify-center text-red-400">
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" /></svg>
          </span>
          Denied
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-5 h-5 rounded border-2 bg-gray-100 border-gray-300 inline-flex items-center justify-center text-gray-400 text-[10px] font-bold">&mdash;</span>
          Using Default
        </div>
      </div>

      {error && (
        <div role="alert" className="mb-4 border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading permissions...</div>
      ) : (
        <PermissionMatrix
          overrides={overrides}
          onChange={handleChange}
          showReset={true}
          onReset={handleReset}
        />
      )}

      <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
        {saved ? (
          <span className="text-sm text-green-600 font-medium">Permissions saved successfully!</span>
        ) : <div />}
        <button
          onClick={handleSave}
          disabled={!hasChanges || saving}
          className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-colors ${
            hasChanges && !saving
              ? 'bg-brand-600 text-white hover:bg-brand-700'
              : 'bg-gray-200 text-gray-400 cursor-not-allowed'
          }`}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};
